/**
 * Chat Image Upload API
 *
 * Posts a downscaled JPEG (base64 data URL) to the main-server, which parks it
 * on Bunny CDN just long enough for the recipient to download it. Once the
 * recipient's client stores the photo locally it acks over the socket and the
 * server deletes the CDN copy — chat history (server keeps none) lives on the
 * two phones, not our storage.
 */

import { serverConfig } from '../network/ServerConfig';

export interface ChatImageUploadResult {
  url: string;
}

/**
 * Upload a JPEG data URL destined for a chat peer.
 * @param imageDataUrl `data:image/jpeg;base64,...` (already downscaled)
 * @param to           Recipient player id (server records it for the ack check)
 * @param token        JWT issued in the auth:success payload
 */
export async function uploadChatImage(
  imageDataUrl: string,
  to: string,
  token: string,
): Promise<ChatImageUploadResult> {
  const resp = await fetch(`${serverConfig.mainServerUrl}/api/chat/image`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ image: imageDataUrl, to }),
  });

  if (!resp.ok) {
    let message = `Upload failed (${resp.status})`;
    try {
      const body = await resp.json();
      if (body?.error) message = body.error;
    } catch {
      // non-JSON error body — keep the status message
    }
    throw new Error(message);
  }

  return resp.json() as Promise<ChatImageUploadResult>;
}
